/* Freeze the page behind a sheet or modal.

   `overflow: hidden` on <body> is the usual answer and it is wrong on iOS: the
   page keeps its scroll position visually but jumps to the top the moment the
   overlay closes, because Safari discards the offset. Pinning the body with
   `position: fixed` and a negative `top` preserves the position exactly, and
   restoring it is a single scrollTo.

   The scrollbar-width compensation matters on desktop: without it, hiding the
   scrollbar widens the viewport by ~15px and the whole page shifts sideways
   as the overlay opens — a jolt on the exact frame the user is watching. */

import { useEffect } from 'react'

let locks = 0
let savedTop = 0
let savedPaddingRight = ''

export function useScrollLock(active: boolean): void {
  useEffect(() => {
    if (!active) return

    // Nested overlays (a confirm inside a sheet) must not each restore on
    // their own close; only the outermost unlock puts the page back.
    locks += 1
    if (locks === 1) {
      const body = document.body
      savedTop = window.scrollY
      const gutter = window.innerWidth - document.documentElement.clientWidth
      savedPaddingRight = body.style.paddingRight

      body.style.position = 'fixed'
      body.style.top = `-${savedTop}px`
      body.style.left = '0'
      body.style.right = '0'
      body.style.width = '100%'
      if (gutter > 0) body.style.paddingRight = `${gutter}px`
    }

    return () => {
      locks -= 1
      if (locks !== 0) return

      const body = document.body
      body.style.position = ''
      body.style.top = ''
      body.style.left = ''
      body.style.right = ''
      body.style.width = ''
      body.style.paddingRight = savedPaddingRight

      // `instant` on purpose: this is a restoration, not a navigation, and a
      // smooth scroll here animates the page under a closing sheet.
      window.scrollTo({ top: savedTop, behavior: 'instant' as ScrollBehavior })
    }
  }, [active])
}
